import { usePermissions } from "@niutraan/hooks";
import { apiClient } from "@niutraan/hooks/src/lib/api";
import {
  PermissionEnum,
  type CreateHubHourAdjustmentDto,
  type HubHourAdjustmentDto,
  type UpdateHubHourAdjustmentDto,
} from "@niutraan/shared-dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
const QUERY_KEYS = {
  hourAdjustments: "hubHourAdjustments",
};

// Get hour adjustments for a specific enrollment
export const useHubHourAdjustments = (enrollmentId: string) => {
  const { hasPermission } = usePermissions();
  const canReadHourAdjustments = hasPermission(
    PermissionEnum.HUB_HOUR_ADJUSTMENT_READ,
  );

  return useQuery({
    queryKey: [QUERY_KEYS.hourAdjustments, enrollmentId],
    queryFn: async () => {
      try {
        const data = await apiClient.get<HubHourAdjustmentDto[]>(
          `/hub/hour-adjustments/enrollments/${enrollmentId}/hour-adjustments`,
        );
        return data;
      } catch (error) {
        console.error("Error fetching hour adjustments:", error);
        throw error;
      }
    },
    // Only enable the query if the user has permission and enrollmentId exists
    enabled: !!enrollmentId && canReadHourAdjustments,
  });
};

// Create a new hour adjustment
export const useCreateHubHourAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (adjustment: CreateHubHourAdjustmentDto) => {
      try {
        const data = await apiClient.post<HubHourAdjustmentDto>(
          "/hub/hour-adjustments",
          adjustment,
        );
        return data;
      } catch (error) {
        console.error("Error creating hour adjustment:", error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the hour adjustments query to refetch the data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.hourAdjustments, variables.hubEnrollmentId],
      });
    },
  });
};

// Update an existing hour adjustment
export const useUpdateHubHourAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateHubHourAdjustmentDto;
    }) => {
      try {
        const response = await apiClient.put<HubHourAdjustmentDto>(
          `/hub/hour-adjustments/${id}`,
          data,
        );
        return response;
      } catch (error) {
        console.error("Error updating hour adjustment:", error);
        throw error;
      }
    },
    onSuccess: (_) => {
      // We need to find the enrollmentId to invalidate the correct query
      // This might require additional context or a separate query
      // For now, we'll invalidate all hour adjustment queries
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.hourAdjustments],
      });
    },
  });
};

// Delete an hour adjustment
export const useDeleteHubHourAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      enrollmentId,
    }: {
      id: string;
      enrollmentId: string;
    }) => {
      try {
        await apiClient.delete(`/hub/hour-adjustments/${id}`);
        return { id, enrollmentId };
      } catch (error) {
        console.error("Error deleting hour adjustment:", error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the hour adjustments query to refetch the data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.hourAdjustments, variables.enrollmentId],
      });
    },
  });
};
